import useCouriersHook from "@/shared/hooks/useCouriersHook";
import useDepositMutation from "@/shared/mutations/schedules/useDepositMutation";
import useDepositStartMoneyMutation from "@/shared/mutations/schedules/useDepositStartMoneyMutation";
import useSchedule from "@/shared/queries/schedules/useSchedule";
import { CmsUsersResponse } from "@/shared/utils/interfaces";
import { useEffect, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "../Button/Button";
import Textfield from "../Input/Textfield";
import Modal, { ModalProps } from "../Modal/Modal";
import SelectBox from "../Select/SelectBox";
import DepositStartMoney from "./DepositStartMoney";
import { formatPrice } from "@/shared/utils/helpers";

const DepositModal = ({ onExit, ...props }: ModalProps) => {
  const mutation = useDepositMutation();
  const couriers = useCouriersHook();
  const startMoney = useDepositStartMoneyMutation();
  const isPending = mutation.isPending ?? startMoney.isPending;
  const [courier, setCourier] = useState<CmsUsersResponse | undefined>(
    undefined
  );
  const schedule = useSchedule(courier?.item_id);
  const { t } = useTranslation("");
  const form = useForm();
  const { reset, register, errors, handleSubmit } = form;
  const onSubmit = ({ partial_deposit, money_at_start_of_day }) => {
    const mutateStartMoney = () => {
      startMoney.mutateAsync({
        account_id: `${courier.item_id}`,
        money_at_start_of_day,
      });
    };
    if (partial_deposit) {
      mutation
        .mutateAsync({
          account_id: `${courier.item_id}`,
          partial_deposit,
        })
        .then(() => {
          if (money_at_start_of_day) {
            mutateStartMoney();
          }
        });
    } else {
      if (money_at_start_of_day) {
        mutateStartMoney();
      }
    }
  };
  useEffect(() => {
    if (schedule && courier) {
      reset({ money_at_start_of_day: schedule?.money_at_start_of_day });
    } else {
      reset({ money_at_start_of_day: undefined });
    }
  }, [courier, schedule]);

  return (
    <Modal onExit={onExit} {...props}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)} className="my-[3rem]">
          <div className="mb-12 grid grid-cols-2 md:grid-cols-4 gap-x-4">
            {couriers.map((c) => (
              <SelectBox
                onClick={() => setCourier(c)}
                active={c.item_id === courier?.item_id}
              >
                {c.name}
              </SelectBox>
            ))}
          </div>

          {courier && (
            <>
              <Textfield
                ref={register({})}
                name="partial_deposit"
                error={errors?.partial_deposit?.message}
                label={t("depositAmount")}
                placeholder={t("depositAmountPlaceholder", {
                  price: formatPrice(5),
                })}
              />
              <div className="mt-[2rem]">
                <DepositStartMoney courier={courier} />
              </div>
            </>
          )}
          <div className="mt-8 flex gap-12">
            <div>
              <Button isPending={isPending} isDisabled={courier === undefined}>
                {t("deposit")}
              </Button>
            </div>

            <Button onClick={onExit}>{t("cancel")}</Button>
          </div>
        </form>
      </FormProvider>
    </Modal>
  );
};

export default DepositModal;
